import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import Select from 'react-select';
import apiClient from '../api/client';

const BRAND = '#0aa375';
const BRAND_LIGHT = '#e6f7f2';
const BRAND_DARK = '#088a62';

const labelStyle = {
  fontSize: '10px', fontWeight: 600, color: '#495057',
  textTransform: 'uppercase', letterSpacing: '0.3px',
  marginBottom: '3px', display: 'block'
};

const inputStyle = {
  height: '28px', fontSize: '12px', padding: '2px 8px',
  border: '1px solid #dee2e6', borderRadius: '4px', width: '100%', outline: 'none',
};

const selectStyles = {
  control: (base) => ({
    ...base, minHeight: '28px', height: '28px', fontSize: '12px',
    borderColor: '#dee2e6', boxShadow: 'none',
    '&:hover': { borderColor: BRAND },
  }),
  valueContainer: (base) => ({ ...base, padding: '0 6px' }),
  indicatorsContainer: (base) => ({ ...base, padding: '0 2px' }),
  dropdownIndicator: (base) => ({ ...base, padding: '0 4px' }),
  option: (base, state) => ({
    ...base, fontSize: '12px',
    backgroundColor: state.isSelected ? BRAND : state.isFocused ? BRAND_LIGHT : 'white',
    color: state.isSelected ? 'white' : '#333',
  }),
  singleValue: (base) => ({ ...base, fontSize: '12px' }),
  placeholder: (base) => ({ ...base, fontSize: '12px', color: '#adb5bd' }),
};

export default function EditTicketModal({ ticket, domain, onClose }) {
  const [companies,        setCompanies]        = useState([]);
  const [subStatuses,      setSubStatuses]      = useState([]);
  const [associateAccount, setAssociateAccount] = useState(false);
  const [selectedCompany,  setSelectedCompany]  = useState(null);
  const [setEstimated,     setSetEstimated]     = useState(false);
  const [estimatedDate,    setEstimatedDate]    = useState('');
  const [setSubStatus,     setSetSubStatus]     = useState(false);
  const [selectedSubStatus,setSelectedSubStatus]= useState(null);
  const [loading,          setLoading]          = useState(true);
  const [updating,         setUpdating]         = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [resComp, resSub] = await Promise.all([
          apiClient.get(`/api/companies?domain=${domain}`),
          apiClient.get(`/api/ticketsubstatuses?domain=${domain}`),
        ]);
        setCompanies(resComp.data.map(c => ({ value: c.id, label: c.text })));
        setSubStatuses(resSub.data.map(s => ({ value: s.value, label: s.label })));
      } catch {
        toast.error('Failed to load edit data');
        onClose();
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [domain]);

  const handleUpdate = async () => {
    setUpdating(true);
    try {
      await apiClient.post(`/api/tickets/update`, {
        domain,
        ticketId: ticket.bgTicketId,
        hasAssociatedAccount: associateAccount,
        associatedAccountId: associateAccount ? selectedCompany?.value : null,
        hasEstimatedDelivery: setEstimated,
        estimatedDelivery: setEstimated ? estimatedDate : null,
        subStatus: setSubStatus ? selectedSubStatus?.value : null,
      });
      toast.success('Ticket updated successfully');
      onClose();
    } catch {
      toast.error('Failed to update ticket');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <>
      <style>{`
        .etm-overlay { position:fixed;inset:0;background:rgba(0,0,0,0.55);z-index:1050;display:flex;align-items:center;justify-content:center; }
        .etm-modal { background:#fff;border-radius:8px;width:min(500px,96vw);display:flex;flex-direction:column;overflow:hidden;box-shadow:0 8px 32px rgba(0,0,0,0.18); }
        .etm-header { background:${BRAND};padding:8px 14px;display:flex;align-items:center;justify-content:space-between;flex-shrink:0; }
        .etm-header h6 { margin:0;color:#fff;font-size:13px;font-weight:700;letter-spacing:0.2px; }
        .etm-header .badge { font-size:11px;color:rgba(255,255,255,0.85);background:rgba(255,255,255,0.18);padding:2px 10px;border-radius:20px;margin-left:8px; }
        .etm-close { background:none;border:none;color:#fff;font-size:18px;line-height:1;cursor:pointer;padding:0 2px;opacity:0.85; }
        .etm-close:hover { opacity:1; }
        .etm-body { padding:12px 14px;display:flex;flex-direction:column;gap:8px; }
        .etm-row { border:1px solid #e9ecef;border-radius:6px;overflow:hidden; }
        .etm-row-header { display:flex;align-items:center;gap:8px;padding:8px 10px;background:#fafafa;cursor:pointer;user-select:none; }
        .etm-row-header:hover { background:${BRAND_LIGHT}; }
        .etm-row-header input[type="checkbox"] { accent-color:${BRAND};width:13px;height:13px;cursor:pointer;flex-shrink:0; }
        .etm-row-label { font-size:12px;color:#212529;line-height:1.4; }
        .etm-row-body { padding:8px 10px;border-top:1px solid #e9ecef;background:#fff; }
        .etm-input-focus:focus { border-color:${BRAND} !important;box-shadow:0 0 0 2px ${BRAND_LIGHT} !important; }
        .etm-btn { height:28px;font-size:11px;font-weight:600;padding:0 16px;border-radius:4px;border:none;cursor:pointer;display:inline-flex;align-items:center;gap:5px; }
        .etm-btn-primary { background:${BRAND};color:#fff; }
        .etm-btn-primary:hover:not(:disabled) { background:${BRAND_DARK}; }
        .etm-btn-primary:disabled { background:#6c757d;cursor:not-allowed; }
        .etm-btn-ghost { background:none;border:none;color:#6c757d;font-size:11px;padding:0 8px;cursor:pointer;height:28px;border-radius:4px; }
        .etm-btn-ghost:hover { background:#f8f9fa; }
        .etm-footer { padding:8px 14px;border-top:1px solid #e9ecef;display:flex;justify-content:flex-end;gap:6px;background:#fafafa; }
        .etm-spinner { width:10px;height:10px;border:2px solid rgba(255,255,255,0.4);border-top-color:#fff;border-radius:50%;animation:etm-spin 0.7s linear infinite;display:inline-block; }
        .etm-spinner-dark { width:18px;height:18px;border:2px solid ${BRAND_LIGHT};border-top-color:${BRAND};border-radius:50%;animation:etm-spin 0.7s linear infinite;display:inline-block; }
        @keyframes etm-spin { to { transform:rotate(360deg); } }
      `}</style>

      <div className="etm-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className="etm-modal">

          {/* Header */}
          <div className="etm-header">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <h6>Edit Ticket</h6>
              {ticket?.szTicketNumber && <span className="badge">{ticket.szTicketNumber}</span>}
            </div>
            <button className="etm-close" onClick={onClose}>×</button>
          </div>

          <div className="etm-body">
            {loading ? (
              <div style={{ textAlign: 'center', padding: '32px' }}>
                <div className="etm-spinner-dark" style={{ margin: '0 auto 8px' }} />
                <div style={{ fontSize: '12px', color: '#6c757d' }}>Loading...</div>
              </div>
            ) : (
              <>
                {/* Row 1 — Associate Account */}
                <div className="etm-row">
                  <div className="etm-row-header" onClick={() => setAssociateAccount(v => !v)}>
                    <input type="checkbox" checked={associateAccount} onChange={e => setAssociateAccount(e.target.checked)} onClick={e => e.stopPropagation()} />
                    <span className="etm-row-label">Associate an account with this ticket</span>
                  </div>
                  {associateAccount && (
                    <div className="etm-row-body">
                      <label style={labelStyle}>Select Account</label>
                      <Select
                        options={companies} value={selectedCompany}
                        onChange={setSelectedCompany}
                        placeholder="-- Select account --"
                        styles={selectStyles} classNamePrefix="react-select"
                        menuPortalTarget={document.body}
                      />
                    </div>
                  )}
                </div>

                {/* Row 2 — Estimated Delivery */}
                <div className="etm-row">
                  <div className="etm-row-header" onClick={() => setSetEstimated(v => !v)}>
                    <input type="checkbox" checked={setEstimated} onChange={e => setSetEstimated(e.target.checked)} onClick={e => e.stopPropagation()} />
                    <span className="etm-row-label">Set estimated delivery date for this ticket</span>
                  </div>
                  {setEstimated && (
                    <div className="etm-row-body">
                      <label style={labelStyle}>Estimated Delivery</label>
                      <input type="date" style={inputStyle} className="etm-input-focus"
                        value={estimatedDate} onChange={e => setEstimatedDate(e.target.value)} />
                    </div>
                  )}
                </div>

                {/* Row 3 — Sub Status */}
                <div className="etm-row">
                  <div className="etm-row-header" onClick={() => setSetSubStatus(v => !v)}>
                    <input type="checkbox" checked={setSubStatus} onChange={e => setSetSubStatus(e.target.checked)} onClick={e => e.stopPropagation()} />
                    <span className="etm-row-label">Set sub status for this ticket</span>
                  </div>
                  {setSubStatus && (
                    <div className="etm-row-body">
                      <label style={labelStyle}>Sub Status</label>
                      <Select
                        options={subStatuses} value={selectedSubStatus}
                        onChange={setSelectedSubStatus}
                        placeholder="-- Select sub status --"
                        styles={selectStyles} classNamePrefix="react-select"
                        menuPortalTarget={document.body}
                      />
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="etm-footer">
            <button className="etm-btn etm-btn-ghost" onClick={onClose}>Close</button>
            <button className="etm-btn etm-btn-primary" onClick={handleUpdate} disabled={loading || updating}>
              {updating ? <><span className="etm-spinner" />Updating...</> : 'Update'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}